import { photos, Photo } from "./photography";

export type SignatureService = {
  id: string;
  number: string;
  name: string;
  hook: string;
  description: string;
  short: string;
  examples: string[];
  ctaLabel: string;
  ctaTo: string;
  image: Photo;
  contactMode: "Rescue" | "Implement" | "Optimize" | "Operate";
  contactArea: string;
  featuredOnHome: boolean;
};

export const signatureServices: SignatureService[] = [
  {
    id: "payroll-911",
    number: "01",
    name: "Payroll 911",
    hook: "When payroll cannot wait.",
    description:
      "Emergency payroll support when payroll is at risk because of a sudden departure, missed deadline, processing breakdown, vendor issue, or other urgent disruption.",
    short: "Emergency coverage when this cycle’s payroll is at risk.",
    examples: [
      "Payroll lead unexpectedly leaves",
      "No internal backup exists",
      "Payroll is due and the process is unclear",
      "Processing has broken down",
      "Immediate continuity coverage is needed",
    ],
    ctaLabel: "Get payroll help",
    ctaTo: "/contact?service=payroll-911",
    image: photos.payroll911,
    contactMode: "Rescue",
    contactArea: "Payroll operations",
    featuredOnHome: true,
  },
  {
    id: "implementation-rescue",
    number: "02",
    name: "Implementation Rescue",
    hook: "When an implementation is going sideways.",
    description:
      "We step into troubled payroll and HRIS implementations, find what’s failing, stabilize the project, correct requirements, configuration, and testing — and get it to a workable go-live.",
    short: "We take over troubled payroll and HRIS projects and get them to a workable go-live.",
    examples: [
      "Implementation is behind schedule",
      "Requirements were missed",
      "Configuration is incorrect",
      "Testing is failing",
      "Parallel payroll exposed problems",
      "Go-live is at risk",
      "System went live but isn’t working correctly",
    ],
    ctaLabel: "Rescue an implementation",
    ctaTo: "/contact?service=implementation-rescue",
    image: photos.implementationRoom,
    contactMode: "Rescue",
    contactArea: "HR technology",
    featuredOnHome: true,
  },
  {
    id: "configuration-review",
    number: "03",
    name: "System Configuration Review",
    hook: "When something feels wrong — but no one can tell you why.",
    description:
      "We review payroll and HRIS configuration against how your organization actually operates, find what’s incorrect or inefficient, and tell you exactly what needs to change.",
    short: "We check your payroll and HRIS setup against how you actually operate — and say what to fix.",
    examples: [
      "Payroll calculations look wrong",
      "Earnings or deductions behave incorrectly",
      "Overtime or differential setup is questionable",
      "Workflows don’t match actual operations",
      "Years of accumulated HRIS changes",
      "The vendor says it’s working as designed — you know it isn’t",
    ],
    ctaLabel: "Review our configuration",
    ctaTo: "/contact?service=configuration-review",
    image: photos.configurationReview,
    contactMode: "Optimize",
    contactArea: "Both payroll and HR technology",
    featuredOnHome: true,
  },
  {
    id: "payroll-operations",
    number: "04",
    name: "Payroll Operations",
    hook: "When you need someone experienced to simply run it correctly.",
    description:
      "Ongoing or interim payroll operations for organizations that need reliable processing, continuity, and experienced coverage.",
    short: "Ongoing or interim payroll, run correctly by experienced people.",
    examples: [
      "Ongoing payroll processing",
      "Interim payroll coverage",
      "Multi-state payroll",
      "Multi-entity payroll",
      "Backup coverage",
      "Team transitions",
      "Fractional payroll leadership",
    ],
    ctaLabel: "Explore Payroll Operations",
    ctaTo: "/payroll-operations",
    image: photos.payrollMaterials,
    contactMode: "Operate",
    contactArea: "Payroll operations",
    featuredOnHome: true,
  },
  {
    id: "implementations",
    number: "05",
    name: "HRIS + Payroll Implementations",
    hook: "Build it correctly from the start.",
    description:
      "We support payroll and HR technology implementations from requirements through configuration, testing, parallel payroll, go-live, and stabilization.",
    short: "Payroll and HRIS implementations, from requirements to stabilization.",
    examples: [
      "Requirements",
      "Configuration",
      "Integrations",
      "Testing and parallel payroll",
      "Go-live",
      "Post-go-live stabilization",
    ],
    ctaLabel: "Explore implementations",
    ctaTo: "/hr-technology",
    image: photos.implementationsPlanned,
    contactMode: "Implement",
    contactArea: "HR technology",
    featuredOnHome: false,
  },
  {
    id: "payroll-tax-rescue",
    number: "06",
    name: "Payroll Tax Rescue",
    hook: "When filings, registrations, or agency issues have gone wrong.",
    description:
      "We help organizations recover from payroll tax failures — sorting out what went wrong, correcting it with the agencies, and putting checks in place so it doesn’t happen again.",
    short: "Late filings, registrations, and agency notices — corrected and resolved.",
    examples: [
      "Missing or late filings",
      "Registration problems",
      "Agency notices",
      "Incorrect reporting",
      "Provider-related filing issues",
      "Corrections and remediation",
      "Follow-up and resolution",
      "Controls to prevent recurrence",
    ],
    ctaLabel: "Get tax support",
    ctaTo: "/contact?service=payroll-tax-rescue",
    image: photos.taxCorrespondence,
    contactMode: "Rescue",
    contactArea: "Payroll operations",
    featuredOnHome: false,
  },
];
